import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { PlanService } from '../../Modules/Plans/Services/plan.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '../../Consts/backoffice.const';
import { ROLE_PLAN_WRITE } from '../../Consts/plans.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { Market } from '../../Models/backoffice/market.model';
import { PlanSummary } from '../../Models/backoffice/plan-summary.model';
import { PlanCreatePayload, PlanFilters } from '../../Modules/Plans/Interfaces/plan-filters.interface';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { MarketListResponse } from '../../Interfaces/api/backoffice/market.interface';
import { PlanListResponse } from '../../Interfaces/api/backoffice/plan.interface';

@Injectable()
export class PlansFacade {
  private readonly _planService = inject(PlanService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _filters = signal<PlanFilters>({ search: '', status: '', market_id: '' });
  private readonly _page = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _isMutating = signal<boolean>(false);

  private readonly _plans = httpResource<LaravelDefaultResponse<PlanListResponse>>(() =>
    this._planService.pagedListRequest(this._filters(), this._page(), this._pageSize()),
  );

  private readonly _markets = httpResource<LaravelDefaultResponse<MarketListResponse>>(() =>
    this._planService.marketsRequest(),
  );

  readonly filters = computed<PlanFilters>(() => this._filters());

  readonly page = computed<number>(() => this._page());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly plans = computed<PlanSummary[]>(
    () => this._planService.toPagedResult(this._resourceState.valueOf(this._plans)).items,
  );

  readonly total = computed<number>(
    () => this._planService.toPagedResult(this._resourceState.valueOf(this._plans)).total,
  );

  readonly markets = computed<Market[]>(() => this._planService.toMarkets(this._resourceState.valueOf(this._markets)));

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._plans.status()),
  );

  readonly isBusy = computed<boolean>(() => this._resourceState.isBusy(this._plans.status()));

  readonly isEmpty = computed<boolean>(() => this.state() === 'ready' && this.plans().length === 0);

  readonly hasActiveFilters = computed<boolean>(() =>
    Object.values(this._filters()).some((value) => value !== ''),
  );

  readonly canWrite = computed<boolean>(() => this._operatorFacade.atLeast(ROLE_PLAN_WRITE));

  applyFilters(filters: PlanFilters): void {
    this._filters.set(filters);
    this._page.set(FIRST_PAGE);
  }

  clearFilters(): void {
    this.applyFilters({ search: '', status: '', market_id: '' });
  }

  goToPage(page: number): void {
    this._page.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._page.set(FIRST_PAGE);
  }

  reload(): void {
    this._plans.reload();
  }

  create(payload: PlanCreatePayload): void {
    this._isMutating.set(true);

    this._planService
      .create(payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(),
        error: (error) => this._failMutation(error),
      });
  }

  private _afterWrite(): void {
    this._isMutating.set(false);
    this._plans.reload();
    this._msgService.showSnackBar('backoffice.plans.detail.created', { type: 'success' });
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
